import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import omit from "lodash.omit";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { LocalStorageKeys } from "../../../core/storage/local-storage";
import { DynamicFormComponent } from "../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../shared/components/notification/notification.service";
import { AnyObject } from "../../../shared/types/app.type";
import { UserInfo } from "../../../shared/types/user-info";
import { UserService } from "../users/user.service";
import { User } from "../users/user.type";
import { CreateProfileService } from "./profile.service";

@Component({
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.css"],
  providers: [CreateProfileService],
})
export class ProfileComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  questions: QuestionBase<string | boolean>[];
  currentUser: UserInfo;
  nameSubscription: Subscription;
  user: User;
  profileForm: FormGroup;
  isEdit: boolean;

  constructor(
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private notificationService: NotificationService,
    private questionsService: CreateProfileService,
    private loader: LoadingService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  ngOnInit(): void {
    this.getUser();
  }

  toggleManageUser(): void {
    this.isEdit = !this.isEdit;
  }

  buildForm(user: User): void {
    const options = {
      user: user,
      currentUser: this.currentUser,
      hidePartner: true,
      hideCompany: true,
    };

    this.questionsService.generateQuestions(options).subscribe((qs) => {
      this.questions = qs;
    });
  }

  onSubmit(user: User): void {
    const data = this.omitFields(user);

    const cleanedData = this.getCleanedData(data);
    const userId = this.user.id;
    this.loader.show();

    this.userService
      .updateUserProfile(userId, cleanedData)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (updateUser: User) => {
          this.notificationService.success("User has been updated");

          this.user = updateUser;
          this.buildForm(updateUser);

          this.currentUser.right = updateUser.right;
          this.currentUser.type = updateUser.type;
          this.currentUser.role = updateUser.role;

          const userSession: UserInfo = JSON.parse(localStorage.getItem(LocalStorageKeys.currentUser));
          const updateUserSession = { ...userSession, ...this.currentUser };
          localStorage.setItem(LocalStorageKeys.currentUser, JSON.stringify(updateUserSession));

          this.toggleManageUser();
        },
        (err) => {
          const error = `There was an error updating Profile:\n ${err}`;
          this.notificationService.error(error);
        },
      )
      .add(() => {
        this.loader.hide();
      });
  }

  /**
   * Remove all properties with empty strings
   * @param user
   * @returns
   */
  private getCleanedData(user: User): AnyObject {
    return Object.fromEntries(Object.entries(user).filter(([_, v]) => v !== ""));
  }
  /**
   * Omit properties
   * @param user
   * @returns
   */
  private omitFields(user: User): User {
    let fields = ["email"];

    if (!this.currentUser.isGlobalAdmin()) {
      fields = ["type", "right", ...fields];
    }

    return omit(user, fields);
  }

  /** Get user */
  private getUser(): void {
    this.userService
      .getUser(this.currentUser.id)
      .subscribe(
        (user) => {
          this.user = user;
          this.buildForm(this.user);
        },
        (error) => {
          this.notificationService.error(error || "Couldn't find User");
        },
      )
      .add(() => this.loader.hide());
  }
}
